import React, { ChangeEvent, useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { ICustomer } from 'dto/users.dto'
import UsersService from 'dataStore/service/users.service'
import { generatePassword } from 'handlers/generatePassword'
import { toast } from 'react-toastify'
import {
  EmailWarning,
  PasswordControls,
  PasswordCopySection,
  PasswordValue,
  StyledDialogActions,
} from './Styles.elements'

interface RequestError {
  response?: {
    data?: {
      message?: string
    }
  }
  data?: {
    message?: string
  }
  message?: string
}

/**
 * Свойства диалога управления паролем клиента.
 * @param open — открыт ли диалог
 * @param customer — выбранный клиент или null
 * @param onClose — обработчик закрытия диалога
 */
interface SetCustomerPasswordDialogProps {
  open: boolean
  customer: ICustomer | null
  onClose: () => void
}

const MINIMUM_PASSWORD_LENGTH = 8
const MAXIMUM_PASSWORD_LENGTH = 32

/**
 * Позволяет администратору установить или удалить пароль клиента.
 * @param props — свойства диалога управления паролем
 */
const SetCustomerPasswordDialog = ({
  open,
  customer,
  onClose,
}: SetCustomerPasswordDialogProps) => {
  const [password, setPassword] = useState('')
  const [savedPassword, setSavedPassword] = useState('')
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isClearing, setIsClearing] = useState(false)

  const customerEmail = customer?.email?.trim?.() ?? ''

  useEffect(() => {
    setPassword('')
    setSavedPassword('')
    setIsPasswordVisible(false)
  }, [customer?.id, open])

  const isRequestPending = isSaving || isClearing
  const hasSelectedCustomer = typeof customer?.id === 'number'
  const isPasswordValid =
    password.length >= MINIMUM_PASSWORD_LENGTH &&
    password.length <= MAXIMUM_PASSWORD_LENGTH

  /**
   * Обновляет пароль из текстового поля.
   * @param changeEvent — событие изменения поля пароля
   */
  const handlePasswordChange = (
    changeEvent: ChangeEvent<HTMLInputElement>
  ): void => {
    setPassword(changeEvent?.target?.value ?? '')
    setSavedPassword('')
  }

  const handleGeneratePassword = (): void => {
    setPassword(generatePassword(12))
    setSavedPassword('')
  }

  /**
   * Переключает видимость пароля в поле ввода.
   */
  const handleTogglePasswordVisibility = (): void => {
    setIsPasswordVisible(previousValue => !previousValue)
  }

  const handleSavePassword = async (): Promise<void> => {
    if (!hasSelectedCustomer || !isPasswordValid) {
      return
    }

    setIsSaving(true)
    try {
      const result = await UsersService.adminSetCustomerPassword(
        customer?.id,
        password
      )
      toast.success(result?.message ?? 'Пароль сохранён')

      if (!customerEmail) {
        setSavedPassword(password)
      } else {
        onClose()
      }
    } catch (error: unknown) {
      const requestError = error as RequestError
      toast.error(
        requestError?.response?.data?.message ??
          requestError?.data?.message ??
          requestError?.message ??
          'Не удалось сохранить пароль'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleClearPassword = async (): Promise<void> => {
    if (
      !hasSelectedCustomer ||
      !window.confirm('Удалить пароль клиента? Это действие нельзя отменить.')
    ) {
      return
    }

    setIsClearing(true)
    try {
      const result = await UsersService.adminClearCustomerPassword(
        customer?.id
      )
      toast.success(result?.message ?? 'Пароль удалён')
      onClose()
    } catch (error: unknown) {
      const requestError = error as RequestError
      toast.error(
        requestError?.response?.data?.message ??
          requestError?.data?.message ??
          requestError?.message ??
          'Не удалось удалить пароль'
      )
    } finally {
      setIsClearing(false)
    }
  }

  const handleCopyPassword = async (): Promise<void> => {
    if (!savedPassword || !navigator?.clipboard?.writeText) {
      toast.error('Не удалось скопировать пароль')
      return
    }

    try {
      await navigator?.clipboard?.writeText(savedPassword)
      toast.success('Пароль скопирован')
    } catch {
      toast.error('Не удалось скопировать пароль')
    }
  }

  const handleClose = (): void => {
    if (!isRequestPending) {
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        Пароль клиента {customer?.first_name ? customer.first_name : ''}
      </DialogTitle>
      <DialogContent>
        <PasswordControls aria-label='Управление паролем клиента'>
          <TextField
            autoFocus
            fullWidth
            label='Пароль'
            type={isPasswordVisible ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            inputProps={{
              minLength: MINIMUM_PASSWORD_LENGTH,
              maxLength: MAXIMUM_PASSWORD_LENGTH,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={
                      isPasswordVisible ? 'Скрыть пароль' : 'Показать пароль'
                    }
                    onClick={handleTogglePasswordVisibility}
                    onMouseDown={mouseDownEvent =>
                      mouseDownEvent?.preventDefault?.()
                    }
                    edge='end'
                    disabled={isRequestPending}
                  >
                    {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText='От 8 до 32 символов'
          />
          <Button
            variant='outlined'
            onClick={handleGeneratePassword}
            disabled={isRequestPending}
          >
            Сгенерировать
          </Button>

          {!customerEmail && (
            <EmailWarning>
              У клиента не указан email. После сохранения скопируйте пароль и
              передайте его клиенту безопасным способом.
            </EmailWarning>
          )}

          {savedPassword && !customerEmail && (
            <PasswordCopySection aria-label='Сохранённый пароль'>
              <PasswordValue>{savedPassword}</PasswordValue>
              <Button variant='outlined' onClick={handleCopyPassword}>
                Скопировать
              </Button>
            </PasswordCopySection>
          )}
        </PasswordControls>
      </DialogContent>
      <StyledDialogActions>
        <Button onClick={handleClose} disabled={isRequestPending}>
          Отмена
        </Button>
        <Button
          color='error'
          onClick={handleClearPassword}
          disabled={isRequestPending || !hasSelectedCustomer}
        >
          {isClearing ? 'Удаление...' : 'Удалить пароль'}
        </Button>
        <Button
          variant='contained'
          onClick={handleSavePassword}
          disabled={
            isRequestPending || !hasSelectedCustomer || !isPasswordValid
          }
        >
          {isSaving ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </StyledDialogActions>
    </Dialog>
  )
}

export default SetCustomerPasswordDialog
